import { buildURLforProviderProcedure } from "pages/CreateOrder/utils/helpers";
import {
  IProviderProcedureResponse,
  ProcedureProviderSearchBody,
  IGroupedProviderProcedureResponse,
} from "types/ProviderProcedure";

import { api } from ".";

export interface IProviderProcedureRequest {
  payerNameField?: string;
  cptCodeField?: string;
  cptDescription?: string;
  providerName?: string;
  specialty?: string;
  connectedHospital?: string;
  page?: number;
  pageSize?: number;
  accountId?: string;
  limit?: number;
}

export interface IProviderProcedureComparisonRequest
  extends IProviderProcedureRequest {
  specialty: string;
  onlyLowerThanMedicare?: boolean;
  volume?: number;
  volumeMatchMode?: string;
}

const providerProcedureQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getProviderProcedure: builder.query<
      IProviderProcedureResponse,
      IProviderProcedureRequest
    >({
      query: ({
        page,
        pageSize,
        payerNameField,
        cptCodeField,
        cptDescription,
        providerName,
        specialty,
        connectedHospital,
      }) => {
        return buildURLforProviderProcedure({
          baseUrl: "provider_procedures",
          payerNameField,
          cptCodeField,
          cptDescription,
          providerName,
          specialty,
          connectedHospital,
          page,
          pageSize,
        });
      },
    }),
    getAllProviderProcedureComparison: builder.query<
      IGroupedProviderProcedureResponse,
      IProviderProcedureComparisonRequest
    >({
      query: ({
        accountId,
        page,
        pageSize,
        payerNameField,
        cptCodeField,
        cptDescription,
        providerName,
        specialty,
        onlyLowerThanMedicare,
        volume,
        volumeMatchMode,
      }) => {
        return buildURLforProviderProcedure({
          baseUrl: `provider_procedures/contract-management/${accountId}`,
          payerNameField,
          cptCodeField,
          cptDescription,
          providerName,
          specialty,
          page,
          pageSize,
          onlyLowerThanMedicare,
          volume,
          volumeMatchMode,
          defaultPagination: false,
        });
      },
    }),
    getTop10ProviderProcedureComparison: builder.query<
      {
        data: {
          enriched: {
            procedureId: number;
            cptCode: string;
            description: string;
            orderCount: number;
          }[];
          overallVarianceAverages: {
            highestVsLowestCommercialAvg: number;
            highestVsMedicareAvg: number;
            highestVsMedicaidAvg: number;
          };
        };
      },
      IProviderProcedureRequest
    >({
      query: ({ accountId, specialty, limit }) => {
        let query = "";

        if (specialty) {
          query += `?specialty=${specialty}`;
        }

        if (limit) {
          query += query ? `&limit=${limit}` : `?limit=${limit}`;
        }

        return {
          url: `provider_procedures/contract-management/top10/${accountId}${query}`,
          method: "GET",
        };
      },
    }),
    getClearProviderProcedureByProvider: builder.mutation<
      IProviderProcedureResponse,
      { providerId: string }
    >({
      query: ({ providerId }) => ({
        url: `provider_procedures/provider/${providerId}`,
        method: "POST",
      }),
    }),
    deleteProviderProcedure: builder.mutation<any, number>({
      query: (id) => ({
        url: `provider_procedures/${id}`,
        method: "DELETE",
      }),
    }),
    deleteAllProviderProcedure: builder.mutation<any, any>({
      query: (filters) => ({
        url: `provider_procedures/`,
        method: "DELETE",
        body: {
          ...filters,
        },
      }),
    }),
    updateProviderProcedurePrice: builder.mutation<
      any,
      {
        id: number;
        priceInCents?: number;
        implantCostInCents?: number | null;
        toBeEdited?: boolean;
        requiresAbn?: boolean;
      }
    >({
      query: ({
        id,
        priceInCents,
        implantCostInCents,
        toBeEdited,
        requiresAbn,
      }) => ({
        url: `provider_procedures/${id}`,
        method: "PUT",
        body: {
          priceInCents,
          implantCostInCents,
          toBeEdited,
          requiresAbn,
        },
      }),
    }),
    searchProviderProcedure: builder.mutation<
      { data: IProviderProcedureResponse },
      ProcedureProviderSearchBody
    >({
      query: ({ accountId, cptCode, payerName }) => ({
        url: `provider_procedures/search`,
        method: "POST",
        body: {
          accountId,
          cptCode,
          payerName,
        },
      }),
    }),
    getCptRecommendations: builder.mutation<
      {
        data: {
          recommendations: {
            cptCode: string;
            cptDescription: string;
            occurrenceRate: number;
            pairedWith: string[];
            existsForPayer: boolean;
          }[];
        };
        message: string;
      },
      { accountId: string; includedCptCodes: string[]; payerName?: string }
    >({
      query: ({ accountId, includedCptCodes, payerName }) => ({
        url: `provider_procedures/cpt-recommendations`,
        method: "POST",
        body: {
          accountId,
          includedCptCodes,
          payerName,
        },
      }),
    }),
  }),
});

export const {
  useGetProviderProcedureQuery,
  useGetAllProviderProcedureComparisonQuery,
  useLazyGetAllProviderProcedureComparisonQuery,
  useGetTop10ProviderProcedureComparisonQuery,
  useGetClearProviderProcedureByProviderMutation,
  useDeleteProviderProcedureMutation,
  useDeleteAllProviderProcedureMutation,
  useUpdateProviderProcedurePriceMutation,
  useSearchProviderProcedureMutation,
  useGetCptRecommendationsMutation,
} = providerProcedureQueries;

export default providerProcedureQueries;
